import { ChangeEvent, FC, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import * as XLSX from 'xlsx';

type ImportWizardWorkspaceProps = {
  tenantQuery: string;
  fixedKind?: BulkWorksheetKind;
  cardTitle?: string;
};

type WorksheetKind =
  | 'locations'
  | 'services'
  | 'providers'
  | 'roles'
  | 'eligibility'
  | 'time-off'
  | 'targets'
  | 'rules'
  | 'coverage-templates'
  | 'unknown';

export type BulkWorksheetKind = Exclude<WorksheetKind, 'coverage-templates' | 'unknown'>;

type WorksheetConfig = {
  label: string;
  importType: string;
  requiredFields: string[];
  fieldOptions: Array<{
    value: string;
    label: string;
    required?: boolean;
  }>;
};

type PreviewRow = Record<string, string>;

type WorksheetDraft = {
  id: string;
  name: string;
  kind: WorksheetKind;
  columns: string[];
  previewRows: PreviewRow[];
  allRows: PreviewRow[];
  totalRows: number;
  mapping: Record<string, string>;
};

type WorksheetValidation = {
  blocking: string[];
  warnings: string[];
  validRows: number;
  invalidRows: number;
};

const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const worksheetConfigs: Record<WorksheetKind, WorksheetConfig> = {
  locations: {
    label: 'Locations',
    importType: 'locations-worksheet',
    requiredFields: ['code', 'display_name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'code', label: 'Location code', required: true },
      { value: 'display_name', label: 'Display name', required: true },
      { value: 'public_label', label: 'Public label' },
      { value: 'location_group', label: 'Location department' },
      { value: 'timezone', label: 'Timezone' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  services: {
    label: 'Services',
    importType: 'services-worksheet',
    requiredFields: ['code', 'display_name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'code', label: 'Service code', required: true },
      { value: 'display_name', label: 'Display name', required: true },
      { value: 'category', label: 'Category' },
      { value: 'notes', label: 'Notes' },
      { value: 'location_codes', label: 'Location codes' },
    ],
  },
  providers: {
    label: 'People',
    importType: 'providers-worksheet',
    requiredFields: ['full_name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'full_name', label: 'Provider name', required: true },
      { value: 'abbreviation', label: 'Abbreviation' },
      { value: 'provider_type', label: 'Provider type' },
      { value: 'primary_role_title', label: 'Primary role title' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  roles: {
    label: 'Roles & Shifts',
    importType: 'roles-worksheet',
    requiredFields: ['code', 'name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'code', label: 'Role code', required: true },
      { value: 'name', label: 'Role name', required: true },
      { value: 'shift_type', label: 'Shift type' },
      { value: 'call_category', label: 'Call category' },
      { value: 'starts_at', label: 'Starts at' },
      { value: 'ends_at', label: 'Ends at' },
    ],
  },
  eligibility: {
    label: 'Eligibility',
    importType: 'eligibility-worksheet',
    requiredFields: ['provider_reference', 'role_reference'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'provider_reference', label: 'Provider reference', required: true },
      { value: 'role_reference', label: 'Role reference', required: true },
      { value: 'service_reference', label: 'Service reference' },
      { value: 'location_reference', label: 'Location reference' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  'time-off': {
    label: 'Time Off',
    importType: 'time-off-worksheet',
    requiredFields: ['provider_reference', 'starts_at'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'provider_reference', label: 'Provider reference', required: true },
      { value: 'starts_at', label: 'Start', required: true },
      { value: 'ends_at', label: 'End' },
      { value: 'status', label: 'Status' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  targets: {
    label: 'Targets',
    importType: 'targets-worksheet',
    requiredFields: ['provider_reference', 'target_value'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'provider_reference', label: 'Provider reference', required: true },
      { value: 'target_value', label: 'Target value', required: true },
      { value: 'target_scope', label: 'Target scope' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  rules: {
    label: 'Rules',
    importType: 'rules-worksheet',
    requiredFields: ['name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'name', label: 'Rule name', required: true },
      { value: 'constraint_type', label: 'Constraint type' },
      { value: 'severity', label: 'Severity' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  'coverage-templates': {
    label: 'Coverage Templates',
    importType: 'coverage-templates-worksheet',
    requiredFields: ['template_name'],
    fieldOptions: [
      { value: 'ignore', label: 'Ignore column' },
      { value: 'template_name', label: 'Template name', required: true },
      { value: 'role_reference', label: 'Role reference' },
      { value: 'location_reference', label: 'Location reference' },
      { value: 'service_reference', label: 'Service reference' },
      { value: 'notes', label: 'Notes' },
    ],
  },
  unknown: {
    label: 'Unknown worksheet',
    importType: 'unknown-workbook',
    requiredFields: [],
    fieldOptions: [{ value: 'ignore', label: 'Ignore column' }],
  },
};

const worksheetSignals: Array<{
  kind: WorksheetKind;
  nameTokens: string[];
  headerTokens: string[];
}> = [
  { kind: 'locations', nameTokens: ['location', 'site', 'facility'], headerTokens: ['location', 'site', 'timezone'] },
  { kind: 'services', nameTokens: ['service', 'department'], headerTokens: ['service', 'coverage', 'department'] },
  { kind: 'providers', nameTokens: ['provider', 'physician', 'doctor', 'surgeon'], headerTokens: ['provider', 'physician', 'doctor', 'abbreviation'] },
  { kind: 'roles', nameTokens: ['role', 'shift'], headerTokens: ['role', 'shift', 'call category'] },
  { kind: 'eligibility', nameTokens: ['eligibility', 'matrix'], headerTokens: ['eligible', 'provider', 'role'] },
  { kind: 'time-off', nameTokens: ['pto', 'vacation', 'time off', 'availability'], headerTokens: ['pto', 'vacation', 'availability', 'start'] },
  { kind: 'targets', nameTokens: ['target', 'fairness', 'quota'], headerTokens: ['target', 'quota', 'fairness'] },
  { kind: 'rules', nameTokens: ['rule', 'constraint'], headerTokens: ['rule', 'constraint', 'severity'] },
  { kind: 'coverage-templates', nameTokens: ['coverage', 'template', 'pattern'], headerTokens: ['template', 'coverage', 'slot'] },
];

const normalizeToken = (value: string) =>
  value
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const detectWorksheetKind = (sheetName: string, headers: string[]): WorksheetKind => {
  const normalizedName = normalizeToken(sheetName);
  const normalizedHeaders = headers.map(normalizeToken);

  let bestMatch: { kind: WorksheetKind; score: number } = { kind: 'unknown', score: 0 };

  for (const signal of worksheetSignals) {
    let score = 0;

    for (const token of signal.nameTokens) {
      if (normalizedName.includes(token)) {
        score += 3;
      }
    }

    for (const token of signal.headerTokens) {
      if (normalizedHeaders.some((header) => header.includes(token))) {
        score += 2;
      }
    }

    if (score > bestMatch.score) {
      bestMatch = { kind: signal.kind, score };
    }
  }

  return bestMatch.score > 0 ? bestMatch.kind : 'unknown';
};

const inferTargetField = (worksheetKind: WorksheetKind, column: string) => {
  const normalizedColumn = normalizeToken(column);
  const options = worksheetConfigs[worksheetKind].fieldOptions;

  const matches: Array<{ token: string; value: string }> = [
    { token: 'code', value: 'code' },
    { token: 'display', value: 'display_name' },
    { token: 'name', value: worksheetKind === 'providers' ? 'full_name' : worksheetKind === 'coverage-templates' ? 'template_name' : 'name' },
    { token: 'public', value: 'public_label' },
    { token: 'group', value: 'location_group' },
    { token: 'time zone', value: 'timezone' },
    { token: 'timezone', value: 'timezone' },
    { token: 'category', value: 'category' },
    { token: 'location code', value: 'location_codes' },
    { token: 'abbr', value: 'abbreviation' },
    { token: 'provider type', value: 'provider_type' },
    { token: 'role', value: worksheetKind === 'eligibility' ? 'role_reference' : 'primary_role_title' },
    { token: 'service', value: 'service_reference' },
    { token: 'location', value: 'location_reference' },
    { token: 'start', value: 'starts_at' },
    { token: 'end', value: 'ends_at' },
    { token: 'target', value: 'target_value' },
    { token: 'scope', value: 'target_scope' },
    { token: 'severity', value: 'severity' },
    { token: 'constraint', value: 'constraint_type' },
    { token: 'note', value: 'notes' },
    { token: 'status', value: 'status' },
    { token: 'shift', value: 'shift_type' },
    { token: 'call', value: 'call_category' },
    { token: 'template', value: 'template_name' },
  ];

  for (const match of matches) {
    if (normalizedColumn.includes(match.token) && options.some((option) => option.value === match.value)) {
      return match.value;
    }
  }

  return 'ignore';
};

const sanitizeCellValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return String(value).trim();
};

const buildNormalizedPayload = (draft: WorksheetDraft, row: PreviewRow) =>
  Object.entries(draft.mapping).reduce<Record<string, string>>((payload, [column, targetField]) => {
    if (targetField === 'ignore') {
      return payload;
    }

    payload[targetField] = sanitizeCellValue(row[column] ?? '');
    return payload;
  }, {});

const validateDraft = (draft: WorksheetDraft): WorksheetValidation => {
  const config = worksheetConfigs[draft.kind];
  const mappedFields = Object.values(draft.mapping).filter((value) => value !== 'ignore');
  const duplicates = mappedFields.filter((value, index) => mappedFields.indexOf(value) !== index);
  const blocking: string[] = [];
  const warnings: string[] = [];

  if (draft.kind === 'unknown') {
    blocking.push('Worksheet type could not be detected. Rename the sheet or adjust its header row before committing.');
  }

  if (duplicates.length > 0) {
    blocking.push('A target field is mapped more than once. Each destination field should come from only one source column.');
  }

  for (const requiredField of config.requiredFields) {
    if (!mappedFields.includes(requiredField)) {
      const option = config.fieldOptions.find((field) => field.value === requiredField);
      blocking.push(`Required field "${option?.label ?? requiredField}" is not mapped.`);
    }
  }

  let invalidRows = 0;

  draft.allRows.forEach((row, index) => {
    const normalizedPayload = buildNormalizedPayload(draft, row);
    const missingRequiredFields = config.requiredFields.filter((field) => sanitizeCellValue(normalizedPayload[field] ?? '') === '');

    if (missingRequiredFields.length > 0) {
      invalidRows += 1;

      if (warnings.length < 5) {
        warnings.push(
          `Preview row ${index + 2} is missing ${missingRequiredFields.join(', ')}.`
        );
      }
    }
  });

  return {
    blocking,
    warnings,
    validRows: Math.max(draft.totalRows - invalidRows, 0),
    invalidRows,
  };
};

const ImportWizardWorkspace: FC<ImportWizardWorkspaceProps> = ({ tenantQuery, fixedKind, cardTitle }) => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<WorksheetDraft[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const validationByDraft = useMemo(
    () =>
      drafts.reduce<Record<string, WorksheetValidation>>((accumulator, draft) => {
        accumulator[draft.id] = validateDraft(draft);
        return accumulator;
      }, {}),
    [drafts]
  );

  const activeDraft = drafts.find((draft) => draft.id === activeDraftId) ?? drafts[0] ?? null;
  const activeValidation = activeDraft ? validationByDraft[activeDraft.id] : null;
  const allBlockingIssues = drafts.flatMap((draft) => validationByDraft[draft.id]?.blocking ?? []);
  const stagedRows = drafts.reduce((total, draft) => total + draft.totalRows, 0);
  const readyRows = drafts.reduce((total, draft) => total + (validationByDraft[draft.id]?.validRows ?? 0), 0);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      const parsedDrafts = workbook.SheetNames.map((sheetName, index) => {
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: '',
          raw: false,
        });
        const firstRow = rows[0] ?? {};
        const columns = Object.keys(firstRow);
        const detectedKind = detectWorksheetKind(sheetName, columns);
        const kind = fixedKind && workbook.SheetNames.length === 1 ? fixedKind : detectedKind;
        const allRows = rows.map((row) =>
          columns.reduce<PreviewRow>((accumulator, column) => {
            accumulator[column] = sanitizeCellValue(row[column]);
            return accumulator;
          }, {})
        );

        return {
          id: `${sheetName}-${index}`,
          name: sheetName,
          kind,
          columns,
          previewRows: allRows.slice(0, 8),
          allRows,
          totalRows: rows.length,
          mapping: columns.reduce<Record<string, string>>((accumulator, column) => {
            accumulator[column] = inferTargetField(kind, column);
            return accumulator;
          }, {}),
        } satisfies WorksheetDraft;
      }).filter((draft) => draft.columns.length > 0);

      const nextDrafts = fixedKind
        ? parsedDrafts.filter((draft) => draft.kind === fixedKind)
        : parsedDrafts;

      if (nextDrafts.length === 0) {
        throw new Error(fixedKind
          ? `No ${worksheetConfigs[fixedKind].label} worksheet was detected. Upload a single ${worksheetConfigs[fixedKind].label} worksheet or name the relevant tab clearly.`
          : 'No tabular rows were detected in the uploaded workbook.');
      }

      setFileName(file.name);
      setDrafts(nextDrafts);
      setActiveDraftId(nextDrafts[0]?.id ?? null);
    } catch (parseError) {
      setDrafts([]);
      setActiveDraftId(null);
      setFileName(file.name);
      setError(parseError instanceof Error ? parseError.message : 'Unable to read the workbook.');
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (draftId: string, column: string, targetField: string) => {
    setDrafts((currentDrafts) =>
      currentDrafts.map((draft) =>
        draft.id === draftId
          ? {
              ...draft,
              mapping: {
                ...draft.mapping,
                [column]: targetField,
              },
            }
          : draft
      )
    );
  };

  const handleCommit = async () => {
    if (drafts.length === 0) {
      return;
    }

    setCommitting(true);
    setError(null);
    setSuccess(null);

    try {
      const params = new URLSearchParams(tenantQuery);
      const batchResponse = await fetch(`/api/v1/secure/schedule-settings/import-batches?${params.toString()}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          name: `${fileName ?? 'Workbook'} import ${new Date().toLocaleString()}`,
          import_type: drafts.length === 1 ? worksheetConfigs[drafts[0].kind].importType : 'multi-sheet-workbook',
          source_filename: fileName,
          status: allBlockingIssues.length === 0 ? 'validated' : 'needs_review',
          worksheet_names: drafts.map((draft) => draft.name),
          column_mapping: drafts.map((draft) => ({
            worksheet_name: draft.name,
            worksheet_type: draft.kind,
            mapping: draft.mapping,
          })),
          validation_summary: {
            blocking_issues: allBlockingIssues.length,
            preview_rows: stagedRows,
            ready_rows: readyRows,
          },
          processed_at: new Date().toISOString(),
        }),
      });

      if (!batchResponse.ok) {
        throw new Error(`Unable to create the import batch (${batchResponse.status}).`);
      }

      const batchPayload = (await batchResponse.json()) as {
        record: {
          id: number;
        };
      };

      const batchId = batchPayload.record.id;

      for (const draft of drafts) {
        for (const [column, targetField] of Object.entries(draft.mapping)) {
          if (targetField === 'ignore') {
            continue;
          }

          const mappingResponse = await fetch(`/api/v1/secure/schedule-settings/import-mappings?${params.toString()}`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify({
              batch_id: batchId,
              worksheet_name: draft.name,
              source_column: column,
              target_field: targetField,
              transform_rule: {
                trim: true,
              },
              required: worksheetConfigs[draft.kind].requiredFields.includes(targetField),
            }),
          });

          if (!mappingResponse.ok) {
            throw new Error(`Unable to save a column mapping for ${draft.name} (${mappingResponse.status}).`);
          }
        }

        for (const [rowIndex, row] of draft.allRows.entries()) {
          const normalizedPayload = buildNormalizedPayload(draft, row);
          const missingRequiredFields = worksheetConfigs[draft.kind].requiredFields.filter(
            (field) => sanitizeCellValue(normalizedPayload[field] ?? '') === ''
          );
          const rowResponse = await fetch(`/api/v1/secure/schedule-settings/import-rows?${params.toString()}`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify({
              batch_id: batchId,
              worksheet_name: draft.name,
              row_number: rowIndex + 2,
              row_key: `${draft.kind}-${rowIndex + 2}`,
              status: missingRequiredFields.length === 0 ? 'validated' : 'needs_review',
              raw_payload: row,
              normalized_payload: normalizedPayload,
              validation_errors: missingRequiredFields.map((field) => `${field} is required.`),
            }),
          });

          if (!rowResponse.ok) {
            throw new Error(`Unable to save preview rows for ${draft.name} (${rowResponse.status}).`);
          }
        }
      }

      setSuccess(
        `Saved ${drafts.length} worksheet${drafts.length === 1 ? '' : 's'} for tenant review with ${stagedRows} preview row${stagedRows === 1 ? '' : 's'}.`
      );
    } catch (commitError) {
      setError(commitError instanceof Error ? commitError.message : 'Unable to commit the import batch.');
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Card className="custom-card schedule-settings-import-wizard">
      <Card.Header className="py-2">
        <div className="card-title">{cardTitle ?? (fixedKind ? `${worksheetConfigs[fixedKind].label} bulk setup` : 'Spreadsheet import')}</div>
      </Card.Header>
      <Card.Body className="p-3">
        <p className="text-muted mb-2">
          {fixedKind
            ? `Upload ${worksheetConfigs[fixedKind].label.toLowerCase()} in bulk, confirm the column mapping, and stage validated rows for the selected Schedule Group.`
            : 'Upload a workbook, review the detected worksheet types, confirm column mappings, and save preview rows for this tenant.'}
        </p>

        <Row className="g-3 align-items-end mb-3">
          <Col lg={7}>
            <Form.Group controlId={`schedule-settings-import-file-${fixedKind ?? 'all'}`}>
              <Form.Label className="fw-semibold">Workbook upload</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(event) => {
                  void handleFileChange(event);
                }}
              />
              <Form.Text className="text-muted">
                Accepted formats: `.xlsx`, `.xls`, and `.csv`. Parsing stays in your browser until you save the workbook rows.
              </Form.Text>
            </Form.Group>
          </Col>
          <Col lg={5}>
            <div className="border rounded-3 bg-light p-2 h-100">
              <p className="fs-12 text-uppercase text-muted mb-2">Current upload snapshot</p>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <Badge bg="" className="bg-primary-transparent text-primary">
                  {drafts.length} worksheet{drafts.length === 1 ? '' : 's'}
                </Badge>
                <Badge bg="" className="bg-success-transparent text-success">
                  {readyRows} ready row{readyRows === 1 ? '' : 's'}
                </Badge>
                <Badge bg="" className="bg-warning-transparent text-warning">
                  {allBlockingIssues.length} blocking issue{allBlockingIssues.length === 1 ? '' : 's'}
                </Badge>
              </div>
              <p className="text-muted mb-0 fs-13">
                {fileName ? `Loaded file: ${fileName}` : 'No workbook loaded yet.'}
              </p>
            </div>
          </Col>
        </Row>

        {loading && (
          <Alert variant="light" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            Reading workbook and inferring worksheet types.
          </Alert>
        )}

        {error && <Alert variant="warning">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {drafts.length > 0 && (
          <>
            <div className="schedule-settings-import-wizard__sheet-tabs mb-4">
              {drafts.map((draft) => {
                const validation = validationByDraft[draft.id];
                const active = draft.id === activeDraft?.id;

                return (
                  <Button
                    key={draft.id}
                    variant={active ? 'primary' : 'light'}
                    className="text-start schedule-settings-import-wizard__sheet-tab"
                    onClick={() => setActiveDraftId(draft.id)}
                  >
                    <div className="fw-semibold">{draft.name}</div>
                    <div className="fs-12">{worksheetConfigs[draft.kind].label}</div>
                    <div className="fs-11">{validation.blocking.length} blockers</div>
                  </Button>
                );
              })}
            </div>

            {activeDraft && activeValidation && (
              <Row className="g-4">
                <Col xl={7}>
                  <Card className="border">
                    <Card.Body>
                      <div className="schedule-settings-import-wizard__sheet-header mb-3">
                        <div>
                          <h5 className="mb-1">{activeDraft.name}</h5>
                          <p className="text-muted mb-0">
                            Detected as <span className="fw-semibold">{worksheetConfigs[activeDraft.kind].label}</span> with {activeDraft.totalRows} parsed row{activeDraft.totalRows === 1 ? '' : 's'}.
                          </p>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <Badge bg="" className="bg-info-transparent text-info">
                            {activeDraft.columns.length} columns
                          </Badge>
                          <Badge bg="" className="bg-success-transparent text-success">
                            {activeValidation.validRows} valid rows
                          </Badge>
                          <Badge bg="" className="bg-warning-transparent text-warning">
                            {activeValidation.invalidRows} needs review
                          </Badge>
                        </div>
                      </div>

                      <Table
                        responsive
                        bordered
                        hover
                        className="align-middle mb-0 schedule-settings-import-wizard__preview-table"
                      >
                        <thead>
                          <tr>
                            {activeDraft.columns.map((column) => (
                              <th key={column}>{column}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {activeDraft.previewRows.map((row, index) => (
                            <tr key={`${activeDraft.id}-row-${index}`}>
                              {activeDraft.columns.map((column) => (
                                <td key={`${activeDraft.id}-${index}-${column}`}>{row[column]}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={5}>
                  <Card className="border mb-4">
                    <Card.Body>
                      <h6 className="mb-3">Column mapping</h6>
                      <div className="d-flex flex-column gap-3">
                        {activeDraft.columns.map((column) => (
                          <Form.Group key={`${activeDraft.id}-${column}`}>
                            <Form.Label className="fw-semibold">{column}</Form.Label>
                            <Form.Select
                              value={activeDraft.mapping[column] ?? 'ignore'}
                              onChange={(event) =>
                                updateMapping(activeDraft.id, column, event.target.value)
                              }
                            >
                              {worksheetConfigs[activeDraft.kind].fieldOptions.map((option) => (
                                <option key={`${column}-${option.value}`} value={option.value}>
                                  {option.label}
                                  {option.required ? ' (required)' : ''}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>

                  <Card className="border">
                    <Card.Body>
                      <h6 className="mb-3">Validation summary</h6>
                      {activeValidation.blocking.length === 0 ? (
                        <Alert variant="success" className="mb-3">
                          Required mappings are present. This sheet can be saved for tenant review.
                        </Alert>
                      ) : (
                        <Alert variant="warning" className="mb-3">
                          Resolve the blocking items below before committing this workbook.
                        </Alert>
                      )}

                      <ul className="ps-3 mb-3">
                        {activeValidation.blocking.map((issue) => (
                          <li key={issue} className="text-danger mb-2">
                            {issue}
                          </li>
                        ))}
                        {activeValidation.warnings.map((warning) => (
                          <li key={warning} className="text-muted mb-2">
                            {warning}
                          </li>
                        ))}
                        {activeValidation.blocking.length === 0 && activeValidation.warnings.length === 0 && (
                          <li className="text-muted">No preview validation warnings for the sampled rows.</li>
                        )}
                      </ul>

                      <Button
                        variant="primary"
                        className="schedule-settings-import-wizard__commit-button"
                        onClick={() => {
                          void handleCommit();
                        }}
                        disabled={committing || drafts.length === 0 || allBlockingIssues.length > 0}
                      >
                        {committing ? 'Saving workbook rows...' : 'Save workbook rows'}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ImportWizardWorkspace;
